import React, { useMemo, useRef, useEffect } from 'react';
import { init, getInstanceByDom, EChartsOption } from 'echarts';
import { debounce } from 'utils/debounce';


export type EChartProps = {
  option: EChartsOption | {
    dataset?: any;
    options?: EChartsOption;
  };
  chartSettings?: {
    renderer?: 'canvas' | 'svg';
    devicePixelRatio?: number;
    width?: number;
    height?: number;
    locale?: string;
  };
  optionSettings?: {
    notMerge?: boolean;
    replaceMerge?: string | string[];
    silent?: boolean;
  };
  style?: React.CSSProperties;
  loading?: boolean;
  events?: Record<string, (param: any) => void>;
  className?: string;
};

const EChart: React.FC<EChartProps> = ({
  option,
  chartSettings = {},
  optionSettings = { notMerge: true },
  style = { width: '100%', height: '350px' },
  loading = false,
  events = {},
  className = '',
  ...props
}) => {
  const chartRef = useRef<HTMLDivElement>(null);

  const resizeChart = useMemo(
    () =>
      debounce(() => {
        if (chartRef.current) {
          const chart = getInstanceByDom(chartRef.current);
          if (chart) {
            chart.resize();
          }
        }
      }, 100),
    []
  );

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = init(chartRef.current, undefined, chartSettings);

    Object.entries(events).forEach(([eventName, handler]) => {
      if (typeof handler === 'function') {
        chart.on(eventName, (param: any) => {
          handler(param);
        });
      }
    });

    let resizeObserver: ResizeObserver | null = null;
    
    if (chartRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        // Wrap in try-catch to suppress "ResizeObserver loop completed with undelivered notifications"
        // This is a harmless browser warning that occurs when ResizeObserver can't deliver all notifications in a single animation frame
        try {
          resizeChart();
        } catch (e) {
          // Ignore ResizeObserver errors
        }
      });
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      chart?.dispose();

      if (resizeObserver && chartRef.current) {
        resizeObserver.unobserve(chartRef.current);
        resizeObserver.disconnect();
      }
    };
  }, [chartSettings, events, resizeChart]);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = getInstanceByDom(chartRef.current);
    
    if (chart && option) {
      if (loading) {
        chart.showLoading();
      } else {
        chart.hideLoading();
        
        let echartsOption: EChartsOption;
        
        if ('options' in option && 'dataset' in option) {
          echartsOption = {
            ...option.options,
            dataset: option.dataset
          } as EChartsOption;
        } else {
          echartsOption = option as EChartsOption;
        }
        
        // Fix series labels and formatters
        if (echartsOption.series) {
          const seriesArray = Array.isArray(echartsOption.series) ? echartsOption.series : [echartsOption.series];
          seriesArray.forEach((s: any) => {
            // Fix Graph Chart edgeLabel formatter
            if (s.type === 'graph' && s.edgeLabel) {
              s.edgeLabel.formatter = (params: any) => params.data?.value || '';
            }
            
            // Fix: convert "{value}" to "{c}" in label formatter string if present
            // LLMs often use {value} which works for axes but not for series labels
            if (s.label && typeof s.label.formatter === 'string') {
              s.label.formatter = s.label.formatter.replace(/{value}/g, '{c}');
            }
          });
        }
        
        chart.setOption(echartsOption, optionSettings);
      }
    }
  }, [option, optionSettings, loading]);

  return (
    <div 
      ref={chartRef} 
      style={style} 
      className={`echart ${className}`.trim()}
      {...props} 
    />
  );
};

export default EChart;
